import type {
  CreateListingDataInput,
  ListingDetailFragment,
  UpdateListingDataInput,
} from "@/graphql/generated/operations";
import { recipientIds, recipientRelations } from "@/lib/content-sharing";
import { accessInput, membersFromAccess, type ListingMemberDraft } from "./listing-access";

// Formularstand einer Ausschreibung (Übersicht und Zugriffsverwaltung) und seine Übersetzung in die
// Eingaben von createListing und updateListing. Rein, damit die Regeln dazu ohne Oberfläche prüfbar
// sind. Die Bilder hat die Fläche "Bilder" (listing-images.ts), Titel und Beschreibung schreibt das
// Dashboard selbst (Autosave), damit nur einer sie schreibt.
//
// Beim Bearbeiten geht nur hinaus, was sich geändert hat: genesis ersetzt Empfänger und Kategorien als
// ganze Liste, sobald das Feld überhaupt mitkommt. Ein unberührtes Feld mitzuschicken hieße, einen Stand
// zurückzuschreiben, den inzwischen jemand anderes geändert haben kann. Die Mitglieder gehen nie mit
// updateListing hinaus, sondern mit setListingAccess (ListingSettingsModal): Inhalt bearbeiten und
// Zugriffe verwalten sind getrennt (@external-adr GENESIS-0095-managing-listing-access-is-separate-from-editing-it).

export interface ListingFormValues {
  name: string;
  description: string;
  /** Wert eines `<input type="datetime-local">`, leer für keine Frist. */
  deadline: string;
  /** Freitext des Zahlenfelds, leer für den Standard von genesis. */
  retentionDays: string;
  categoryIds: string[];
  sharedWith: string[];
  publiclyReadable: boolean;
  members: ListingMemberDraft[];
}

export const EMPTY_LISTING_FORM: ListingFormValues = {
  name: "",
  description: "",
  deadline: "",
  retentionDays: "",
  categoryIds: [],
  sharedWith: [],
  // Eine neue Ausschreibung ist ein Entwurf und liest ohnehin niemand außer den Mitgliedern; ob sie
  // später öffentlich wird, ist eine eigene Entscheidung und kein stiller Standard.
  publiclyReadable: false,
  members: [],
};

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** Ein Zeitpunkt als Wert für `<input type="datetime-local">`, in der Zeitzone des Betrachters. */
export function toLocalDateTimeInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Zurück in einen Zeitpunkt. Leer heißt keine Frist; Unlesbares ebenfalls null. */
export function fromLocalDateTimeInput(value: string): string | null {
  if (!value.trim()) return null;
  // Ohne Zonenangabe liest Date ein Datum mit Uhrzeit als Ortszeit, also so, wie es im Feld steht.
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Same 1–180 day product range as Genesis ListingLifecycle. */
export const MAX_RETENTION_DAYS = 180;

/** Leer: kein Wert. Sonst eine ganze Zahl von 1 bis 180, oder "invalid". */
export function parseRetentionDays(value: string): number | null | "invalid" {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (!/^\d+$/.test(trimmed)) return "invalid";
  const days = Number(trimmed);
  return days >= 1 && days <= MAX_RETENTION_DAYS ? days : "invalid";
}

export function listingFormErrors(values: ListingFormValues): { name: boolean; retentionDays: boolean } {
  return {
    name: !values.name.trim(),
    retentionDays: parseRetentionDays(values.retentionDays) === "invalid",
  };
}

export function formValuesOf(listing: ListingDetailFragment): ListingFormValues {
  return {
    name: listing.name ?? "",
    description: listing.description ?? "",
    deadline: toLocalDateTimeInput(listing.deadline),
    retentionDays: listing.retentionDays != null ? String(listing.retentionDays) : "",
    categoryIds: listing.categories.map((category) => category.id),
    sharedWith: recipientIds(listing.sharedWith),
    publiclyReadable: listing.publiclyReadable === true,
    members: membersFromAccess(listing.access, listing.createdBy?.id),
  };
}

export function createListingData(values: ListingFormValues): CreateListingDataInput {
  const retention = parseRetentionDays(values.retentionDays);
  const deadline = fromLocalDateTimeInput(values.deadline);
  return {
    name: values.name.trim(),
    description: values.description.trim() || null,
    ...(deadline ? { deadline } : {}),
    ...(typeof retention === "number" ? { retentionDays: retention } : {}),
    ...(values.categoryIds.length ? { categoryIds: values.categoryIds } : {}),
    sharedWith: recipientRelations(values.sharedWith),
    publiclyReadable: values.publiclyReadable,
    ...(values.members.length ? { access: accessInput(values.members) } : {}),
  };
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  const left = [...new Set(a)].sort();
  const right = [...new Set(b)].sort();
  return left.length === right.length && left.every((value, i) => value === right[i]);
}

export function updateListingData(initial: ListingFormValues, values: ListingFormValues): UpdateListingDataInput {
  const data: UpdateListingDataInput = {};
  if (values.deadline !== initial.deadline) {
    data.deadline = fromLocalDateTimeInput(values.deadline);
  }
  const retention = parseRetentionDays(values.retentionDays);
  // Leeren heißt beim Bearbeiten "nicht ändern": eine Aufbewahrung muss größer als null sein, und
  // den Standard kennt nur genesis.
  if (typeof retention === "number" && values.retentionDays.trim() !== initial.retentionDays.trim()) {
    data.retentionDays = retention;
  }
  if (!sameSet(values.categoryIds, initial.categoryIds)) {
    data.categoryIds = values.categoryIds;
  }
  if (!sameSet(values.sharedWith, initial.sharedWith) || values.publiclyReadable !== initial.publiclyReadable) {
    data.sharedWith = recipientRelations(values.sharedWith);
    data.publiclyReadable = values.publiclyReadable;
  }
  return data;
}

/**
 * Eine Ablehnung aus `Validate*Result.issues`. Deren Text ist Produkttext von genesis und darf so
 * angezeigt werden (@adr SPARK-0010-no-raw-server-errors-in-ui@1); alles andere geht durch
 * userErrorMessage.
 */
export class ValidationRejected extends Error {}

export function throwIfRejected(
  output: { __typename: string; issues?: ReadonlyArray<{ message: string }> } | null | undefined,
  fallback: string,
): void {
  if (output?.__typename !== "ValidateListingResult") return;
  const message = (output.issues ?? []).map((issue) => issue.message).filter(Boolean).join(" · ");
  throw new ValidationRejected(message || fallback);
}
